
import React, { useEffect, useRef, memo } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default markers in Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;

// Create optimized SVG pin with different styles
const createOptimizedPin = (color: string = '#3b82f6', category: string = 'default') => {
  const pinSvg = `
    <svg width="32" height="40" viewBox="0 0 32 40" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M16 0C7.16344 0 0 7.16344 0 16C0 25.5 16 40 16 40C16 40 32 25.5 32 16C32 7.16344 24.8366 0 16 0Z" fill="${color}"/>
      <path d="M16 0C7.16344 0 0 7.16344 0 16C0 25.5 16 40 16 40C16 40 32 25.5 32 16C32 7.16344 24.8366 0 16 0Z" fill="url(#gradient-${category})"/>
      <circle cx="16" cy="16" r="8" fill="white" fill-opacity="0.9"/>
      <circle cx="16" cy="16" r="4" fill="${color}"/>
      <defs>
        <linearGradient id="gradient-${category}" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" style="stop-color:${color};stop-opacity:1" />
          <stop offset="100%" style="stop-color:${color};stop-opacity:0.8" />
        </linearGradient>
      </defs>
    </svg>
  `;
  
  return L.divIcon({
    className: 'custom-pin-marker',
    html: `<div class="pin-container animate-bounce-gentle">${pinSvg}</div>`,
    iconSize: [32, 40],
    iconAnchor: [16, 40],
    popupAnchor: [0, -40]
  });
};

// Category color mapping
const getCategoryColor = (category?: string): string => {
  const colors = {
    tech: '#3b82f6',
    ecommerce: '#10b981',
    consulting: '#6b7280',
    health: '#ef4444',
    creative: '#8b5cf6',
    fitness: '#f59e0b',
    restaurant: '#f97316',
    legal: '#1f2937',
    education: '#3b82f6',
    realestate: '#059669',
    beauty: '#ec4899',
    software: '#6366f1',
    music: '#f59e0b',
    sports: '#ea580c',
    default: '#3b82f6'
  };
  return colors[category as keyof typeof colors] || colors.default;
};

interface LeafletMapProps {
  latitude: number;
  longitude: number;
  zoom?: number;
  className?: string;
  category?: string;
  popupText?: string;
}

const LeafletMap = memo(({ 
  latitude, 
  longitude, 
  zoom = 15, 
  className = "",
  category = 'default',
  popupText = 'Nossa localização'
}: LeafletMapProps) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const markerRef = useRef<L.Marker | null>(null);

  // Validate coordinates
  if (!latitude || !longitude || isNaN(latitude) || isNaN(longitude)) {
    return (
      <div className={`w-full h-40 rounded-xl bg-gray-100 flex items-center justify-center ${className}`}>
        <div className="text-gray-500 text-sm animate-pulse">Coordenadas inválidas</div>
      </div>
    );
  }

  useEffect(() => {
    if (!mapRef.current) return;

    try {
      // Clean up existing instances
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
      }

      // Create map instance
      const map = L.map(mapRef.current, {
        scrollWheelZoom: false,
        zoomControl: true,
        attributionControl: false
      }).setView([latitude, longitude], zoom);

      // Add tile layer with subtle styling
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap',
        className: 'map-tiles'
      }).addTo(map);

      // Create optimized marker
      const categoryColor = getCategoryColor(category);
      const marker = L.marker([latitude, longitude], { 
        icon: createOptimizedPin(categoryColor, category)
      }).addTo(map);

      // Add popup
      marker.bindPopup(`
        <div class="map-popup">
          <div class="font-medium text-gray-900">${popupText}</div>
          <div class="text-xs text-gray-600 mt-1">Clique para navegar</div>
        </div>
      `);

      mapInstanceRef.current = map;
      markerRef.current = marker;

      // Add click handler for navigation
      marker.on('click', () => {
        const url = `https://www.google.com/maps?q=${latitude},${longitude}`;
        window.open(url, '_blank');
      });

    } catch (error) {
      console.error('Erro ao inicializar mapa:', error);
    }

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
        markerRef.current = null;
      }
    };
  }, [latitude, longitude, zoom, category, popupText]);

  return (
    <div className={`w-full h-40 rounded-xl overflow-hidden shadow-sm border border-gray-200 ${className}`}>
      <div 
        ref={mapRef} 
        className="w-full h-full"
      />
    </div>
  );
});

LeafletMap.displayName = 'LeafletMap';

export default LeafletMap;
